// Shared seed-by-grass mapping: each supported species -> its enthusiast-grade
// catalog seed (data/guide-product-recommendations.ts) plus the "why it fits"
// reason, and the planting-time STARTER companion. Extracted from the seeding
// calculator so every grass surface (seeding calculator, grass comparison) reads
// one source of truth for "the seed for THIS grass".
//
// St. Augustine is intentionally unmapped — it is established from sod/plugs, not
// seed — so callers return no curated seed pick for it rather than recommend a
// seed that does not exist.

import type { CatalogPick } from './types'

// Lowercase, letters-only, collapsed-whitespace form of the grass name, so
// "Kentucky Bluegrass", "Bermudagrass", "St. Augustinegrass", and slug variants
// ("kentucky-bluegrass") all round-trip to one key.
export function normalizeGrass(grassType: string): string {
  return grassType.toLowerCase().replace(/[^a-z]+/g, ' ').replace(/\s+/g, ' ').trim()
}

// Normalized grass name -> that species' catalog seed key + why it fits.
export const SEED_BY_GRASS: Record<string, CatalogPick> = {
  'kentucky bluegrass': {
    key: 'seed-nightshift-kbg',
    reason: 'Kentucky bluegrass seed — thick, self-repairing cool-season turf with strong cold tolerance.',
  },
  'tall fescue': {
    key: 'seed-nightshift-fescue',
    reason: 'Black Beauty tall-fescue blend — deep-rooted and drought-tolerant, the cool-season workhorse.',
  },
  'fine fescue': {
    key: 'seed-fescue-jonathan-green',
    reason: 'A true fine-fescue blend (hard, chewings, creeping red) — soft, shade-tolerant, low-input turf.',
  },
  'perennial ryegrass': {
    key: 'seed-nightshift-ryegrass',
    reason: 'Perennial ryegrass — fast germination and excellent wear tolerance for cool-season lawns.',
  },
  bermudagrass: {
    key: 'seed-bermuda',
    reason: 'Heat-loving Bermuda seed for full-sun southern lawns.',
  },
  zoysiagrass: {
    key: 'seed-zoysia-zenith',
    reason: 'Seeded zoysia (Zenith) for sunny warm-season lawns — dense, wear-tolerant turf.',
  },
  centipede: {
    key: 'seed-centipede-pennington',
    reason: 'Low-maintenance centipede seed for acidic, sunny southeastern lawns.',
  },
  bahiagrass: {
    key: 'seed-bahia-pennington',
    reason: 'Pensacola bahia seed for low-input lawns with sandy soil, heat, and drought.',
  },
  buffalograss: {
    key: 'seed-buffalo-outsidepride',
    reason: 'Seeded buffalograss for low-water, full-sun lawns in prairie and western climates.',
  },
}

// Companion: every new seeding / overseed benefits from a planting-time
// phosphorus boost.
export const STARTER: CatalogPick = {
  key: 'starter-scotts',
  reason: 'Starter fertilizer with the planting-time phosphorus boost new seedlings need to root in.',
}
